// frontend/src/components/OwnerDashboard/FleetMapPanel.js
//
// Every tracked vehicle on one map, on the dashboard.
//
// Before this, seeing where a car was meant opening /vehicles/:id and scrolling
// to VehicleTelematicsPanel — one page per car. That panel is still the deep
// view (driving score, crash history, protection status); this is the shallow
// one: where is everything, right now, without navigating.
//
// It reads GET /telematics/fleet, which returns one row per owned vehicle in a
// single call rather than one summary request per car.

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { GoogleMap, Marker, useJsApiLoader } from '@react-google-maps/api';
import { signal } from '../../theme/iparc';
import { getFleetPositions, requestDevice } from '../../api/ownerTelematics';

const GOOGLE_MAPS_API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
const POLL_MS = 30 * 1000;

// Abidjan — only used when no vehicle has ever reported a position, so the map
// renders somewhere sensible instead of the middle of the Atlantic.
const FALLBACK_CENTER = { lat: 5.3599, lng: -4.0083 };

// Google's default map is white. These flatten it to the dashboard's ink
// palette so the panel does not glow next to everything around it.
const DARK_MAP_STYLE = [
  { elementType: 'geometry', stylers: [{ color: '#17140F' }] },
  { elementType: 'labels.text.stroke', stylers: [{ color: '#131110' }] },
  { elementType: 'labels.text.fill', stylers: [{ color: '#8F877B' }] },
  { featureType: 'poi', stylers: [{ visibility: 'off' }] },
  { featureType: 'transit', stylers: [{ visibility: 'off' }] },
  { featureType: 'road', elementType: 'geometry', stylers: [{ color: '#241F1B' }] },
  { featureType: 'road', elementType: 'labels.text.fill', stylers: [{ color: '#6E665C' }] },
  { featureType: 'road.highway', elementType: 'geometry', stylers: [{ color: '#2E2822' }] },
  { featureType: 'water', elementType: 'geometry', stylers: [{ color: '#0D0C0B' }] },
  { featureType: 'administrative', elementType: 'geometry', stylers: [{ color: '#2E2822' }] },
];

// ── pure helpers (unit-tested) ──────────────────────────────────────────────

// Three states an owner can act on, in the dashboard's signal colours:
// moving now, parked but reporting, and silent.
export function vehicleState(row) {
  if (!row || !row.device) return 'none';
  if (!row.device.live) return 'offline';
  return row.position && !row.position.parked ? 'moving' : 'parked';
}

export const STATE_COLOR = {
  moving: signal.ok,      // #74A98D
  parked: '#C9C3B6',
  offline: '#6E665C',
  none: '#5C554C',
};

// Plate text on the map. Green regardless of the vehicle's state — the dot
// beside it already carries the state, and a plate that changed colour with it
// would say the same thing twice while making a silent car's plate the hardest
// one to read. Selecting a vehicle brightens it rather than recolouring it.
export const PLATE_GREEN = signal.ok;            // #74A98D
export const PLATE_GREEN_SELECTED = '#A6D6BC';

export const STATE_LABEL = {
  moving: 'en route',
  parked: 'stationné',
  offline: 'hors ligne',
  none: 'sans boîtier',
};

// "Bouaké · 62 km/h" / "Abidjan · stationné" / "vu il y a 3 h"
export function positionLabel(row, now = Date.now()) {
  const state = vehicleState(row);
  if (state === 'none') return 'Aucun boîtier installé';
  if (!row.position) return 'En attente du premier signal';

  const place = row.position.placeName
    || `${row.position.lat.toFixed(3)}, ${row.position.lng.toFixed(3)}`;
  if (state === 'moving') return `${place} · ${Math.round(row.position.speedKmh || 0)} km/h`;
  if (state === 'parked') return `${place} · stationné`;
  return `${place} · ${sinceLabel(row.device.lastSeenAt, now)}`;
}

// Deliberately coarse: a GPS box that reports every few seconds makes
// "il y a 41 secondes" noise, and anything over a day only matters as a day.
export function sinceLabel(at, now = Date.now()) {
  if (!at) return 'jamais vu';
  const mins = Math.floor((now - new Date(at).getTime()) / 60000);
  if (mins < 1) return "à l'instant";
  if (mins < 60) return `vu il y a ${mins} min`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `vu il y a ${hours} h`;
  const days = Math.floor(hours / 24);
  return `vu il y a ${days} j`;
}

// ── clustering ──────────────────────────────────────────────────────────────
//
// Three cars parked in the same yard overnight land on the same pixel and read
// as one car. The library ships a clusterer, but it draws CDN-hosted blue pin
// images that would fight the palette, and it wants imperative Marker
// instances. This is the same idea in ~30 lines of pure function, which also
// means it can be tested without a map.
//
// Web Mercator, the projection Google Maps uses: the whole world is one 256px
// tile at zoom 0 and doubles each zoom level. Implemented here rather than read
// off map.getProjection() so clustering stays a pure function of (rows, zoom).
const TILE = 256;
export function projectMercator(lat, lng) {
  const siny = Math.min(Math.max(Math.sin((lat * Math.PI) / 180), -0.9999), 0.9999);
  return {
    x: TILE * (0.5 + lng / 360),
    y: TILE * (0.5 - Math.log((1 + siny) / (1 - siny)) / (4 * Math.PI)),
  };
}

// Greedy grid clustering in screen pixels. Seeds each cluster from the first
// unclaimed row, so with sortRows() upstream the anchor is always the most
// actionable vehicle in the group and the output is deterministic — a cluster
// that renumbered itself every 30s poll would be worse than overlapping dots.
export function clusterRows(rows, zoom, radiusPx = 46) {
  const list = (rows || []).filter((r) => r && r.position);
  if (list.length === 0) return [];
  const scale = Math.pow(2, Number.isFinite(zoom) ? zoom : 12);

  const pts = list.map((r) => {
    const p = projectMercator(r.position.lat, r.position.lng);
    return { row: r, x: p.x * scale, y: p.y * scale };
  });

  const taken = new Array(pts.length).fill(false);
  const out = [];
  for (let i = 0; i < pts.length; i++) {
    if (taken[i]) continue;
    taken[i] = true;
    const members = [pts[i].row];
    for (let j = i + 1; j < pts.length; j++) {
      if (taken[j]) continue;
      const dx = pts[j].x - pts[i].x;
      const dy = pts[j].y - pts[i].y;
      if (dx * dx + dy * dy <= radiusPx * radiusPx) { taken[j] = true; members.push(pts[j].row); }
    }
    out.push({
      key: members.map((r) => String(r.vehicleId)).join('|'),
      rows: members,
      lat: members.reduce((s, r) => s + r.position.lat, 0) / members.length,
      lng: members.reduce((s, r) => s + r.position.lng, 0) / members.length,
    });
  }
  return out;
}

// A cluster takes the colour of its most active member: one car on the road in
// a yard full of parked ones is the thing worth noticing.
export function clusterState(cluster) {
  const states = (cluster.rows || []).map(vehicleState);
  if (states.includes('moving')) return 'moving';
  if (states.includes('parked')) return 'parked';
  return 'offline';
}

// What the panel shows at all.
//
// 'hidden' is only for "we do not know yet" and "this owner has no vehicles".
// An owner WITH vehicles but no tracker gets 'empty', not 'hidden' — the first
// version returned null there, and a panel that renders nothing is
// indistinguishable from a feature that never deployed.
export function panelMode(data) {
  if (!data || !Array.isArray(data.vehicles) || data.vehicles.length === 0) return 'hidden';
  return data.equipped === 0 ? 'empty' : 'map';
}

// Rows worth drawing, most actionable first: moving, then parked, then silent,
// then unequipped. An owner scanning the list wants the car on the road first.
const ORDER = { moving: 0, parked: 1, offline: 2, none: 3 };
export function sortRows(rows) {
  return [...(rows || [])].sort((a, b) => {
    const d = ORDER[vehicleState(a)] - ORDER[vehicleState(b)];
    return d !== 0 ? d : String(a.plateNumber || '').localeCompare(String(b.plateNumber || ''));
  });
}

// ── component ───────────────────────────────────────────────────────────────

// One chip per untracked vehicle. Requesting is per-vehicle server-side
// (POST /telematics/device-requests takes a vehicleId), so this does not offer
// a single fleet-wide button that would quietly fan out into N requests.
function DeviceRequestChip({ vehicle, token }) {
  const [state, setState] = useState('idle'); // idle | sending | sent | error

  const send = async () => {
    setState('sending');
    try {
      await requestDevice(vehicle.vehicleId, token);
      setState('sent');
    } catch (e) {
      setState('error');
    }
  };

  const plate = vehicle.plateNumber || 'Véhicule';
  if (state === 'sent') {
    return (
      <span className="inline-flex items-center gap-1.5 h-[28px] px-2.5 rounded-[8px] border border-[#241F1B] text-[11px]"
            style={{ color: signal.ok }}>
        {plate} · demande envoyée
      </span>
    );
  }

  return (
    <button
      onClick={send}
      disabled={state === 'sending'}
      className="h-[28px] px-2.5 rounded-[8px] border border-[#241F1B] text-[11px] text-[#8F877B]
                 hover:text-[#F4F1EA] hover:border-[#3A332C] disabled:opacity-50 transition-colors"
      style={state === 'error' ? { color: signal.danger } : undefined}
    >
      {plate} · {state === 'error' ? 'réessayer' : 'demander un boîtier'}
    </button>
  );
}

// `fetcher` swaps the data source without forking the component: the owner
// dashboard passes nothing and gets its own vehicles, the admin dashboard
// passes getAllFleetPositions and gets the platform. `title` and `emptyState`
// follow, because "Aucun boîtier sur VOS véhicules" is the wrong sentence for
// an operator looking at everybody's.
export default function FleetMapPanel({
  token, onOpenVehicle, className = '',
  fetcher = getFleetPositions,
  title = 'Position de la flotte',
  canRequestDevice = true,
}) {
  const { isLoaded } = useJsApiLoader({ googleMapsApiKey: GOOGLE_MAPS_API_KEY });
  const [data, setData] = useState(null);
  const [selected, setSelected] = useState(null);
  // Clustering is a function of zoom, so the map has to tell us when it moves.
  const [zoom, setZoom] = useState(12);
  const mapRef = useRef(null);
  const fittedRef = useRef(false);

  const load = useCallback(async () => {
    try {
      setData(await fetcher(token));
    } catch (e) {
      // Keep the last good fix on a transient error — a blank map is worse
      // than a 30-second-old one.
    }
  }, [token, fetcher]);

  useEffect(() => {
    load();
    const id = setInterval(load, POLL_MS);
    return () => clearInterval(id);
  }, [load]);

  const rows = useMemo(() => sortRows(data?.vehicles), [data]);
  const located = useMemo(() => rows.filter((r) => r.position), [rows]);
  const clusters = useMemo(() => clusterRows(located, zoom), [located, zoom]);

  // Fit to every marker once, then leave the viewport alone: re-fitting on each
  // 30s poll would yank the map out from under someone who has panned or zoomed.
  const onMapLoad = useCallback((map) => {
    mapRef.current = map;
    const z = map.getZoom();
    if (Number.isFinite(z)) setZoom(z);
  }, []);
  const onZoomChanged = useCallback(() => {
    const z = mapRef.current && mapRef.current.getZoom();
    if (Number.isFinite(z)) setZoom(z);
  }, []);
  useEffect(() => {
    if (!isLoaded || !mapRef.current || fittedRef.current || located.length === 0) return;
    if (located.length === 1) {
      mapRef.current.setCenter({ lat: located[0].position.lat, lng: located[0].position.lng });
      mapRef.current.setZoom(14);
    } else {
      const bounds = new window.google.maps.LatLngBounds();
      located.forEach((r) => bounds.extend({ lat: r.position.lat, lng: r.position.lng }));
      mapRef.current.fitBounds(bounds, 48);
    }
    fittedRef.current = true;
  }, [isLoaded, located]);

  const focus = (row) => {
    setSelected(String(row.vehicleId));
    if (row.position && mapRef.current) {
      mapRef.current.panTo({ lat: row.position.lat, lng: row.position.lng });
      mapRef.current.setZoom(15);
    }
  };

  // Clicking a bubble zooms into what it is hiding, rather than selecting an
  // arbitrary one of its members.
  const openCluster = (cluster) => {
    if (!mapRef.current || !window.google) return;
    const bounds = new window.google.maps.LatLngBounds();
    cluster.rows.forEach((r) => bounds.extend({ lat: r.position.lat, lng: r.position.lng }));
    // Cars parked side by side share a coordinate to 5dp, so the bounds can be
    // a single point — fitBounds on that zooms to the maximum. Step in instead.
    if (bounds.getNorthEast().equals(bounds.getSouthWest())) {
      mapRef.current.panTo(bounds.getCenter());
      mapRef.current.setZoom(Math.min(19, (mapRef.current.getZoom() || 12) + 3));
    } else {
      mapRef.current.fitBounds(bounds, 64);
    }
  };

  const mode = panelMode(data);

  // Until the first fetch lands, and for an owner with no vehicles at all (the
  // dashboard shows its own empty state for that).
  if (mode === 'hidden') return null;

  // No tracker fitted anywhere. The panel used to return null here, which was
  // wrong: "nothing on screen" is indistinguishable from "the feature did not
  // ship", and that is exactly how it read the first time it went out. Say what
  // is missing instead.
  if (mode === 'empty') {
    return (
      <div className={`rounded-2xl border border-[#241F1B] bg-gradient-to-b from-[#17140F] to-[#131110] overflow-hidden ${className}`}>
        <header className="flex items-center justify-between px-[18px] py-3.5 border-b border-[#241F1B]">
          <p className="text-[10.5px] uppercase tracking-[.16em] text-[#6E665C]">{title}</p>
          <span className="text-[11px] text-[#6E665C] tabular-nums">0 équipé</span>
        </header>
        <div className="px-[18px] py-4">
          <p className="text-[12.5px] text-[#C9C3B6]">
            {canRequestDevice
              ? 'Aucun boîtier GPS n’est installé sur vos véhicules.'
              : 'Aucun boîtier GPS actif sur la plateforme.'}
          </p>
          <p className="text-[11px] text-[#6E665C] mt-1 leading-relaxed">
            {canRequestDevice
              ? 'Une fois un boîtier posé, la position de chaque véhicule s’affiche ici, en direct.'
              : 'Liez un boîtier à un véhicule depuis Télématique pour le voir apparaître ici.'}
          </p>
          {canRequestDevice && (
            <ul className="mt-3 flex flex-wrap gap-2">
              {rows.map((r) => (
                <li key={String(r.vehicleId)}>
                  <DeviceRequestChip vehicle={r} token={token} />
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    );
  }

  const center = located.length
    ? { lat: located[0].position.lat, lng: located[0].position.lng }
    : FALLBACK_CENTER;

  return (
    <div className={`rounded-2xl border border-[#241F1B] bg-gradient-to-b from-[#17140F] to-[#131110] overflow-hidden ${className}`}>
      <header className="flex items-center justify-between px-[18px] py-3.5 border-b border-[#241F1B]">
        <p className="text-[10.5px] uppercase tracking-[.16em] text-[#6E665C]">{title}</p>
        <span className="text-[11px] text-[#8F877B] tabular-nums">
          <span style={{ color: data.live > 0 ? signal.ok : '#6E665C' }}>{data.live}</span>
          {` en direct · ${data.equipped} équipé${data.equipped > 1 ? 's' : ''}`}
        </span>
      </header>

      <div className="flex flex-wrap">
        <div className="flex-1 min-w-[300px] basis-[520px] border-r border-[#241F1B]">
          {isLoaded ? (
            <GoogleMap
              mapContainerStyle={{ width: '100%', height: '320px' }}
              center={center}
              zoom={12}
              onLoad={onMapLoad}
              onZoomChanged={onZoomChanged}
              options={{
                disableDefaultUI: true, zoomControl: true, gestureHandling: 'cooperative',
                styles: DARK_MAP_STYLE, backgroundColor: '#0D0C0B',
              }}
            >
              {clusters.map((c) => {
                const state = clusterState(c);
                const grouped = c.rows.length > 1;
                const r = c.rows[0];
                const isSel = !grouped && selected === String(r.vehicleId);
                const size = grouped ? Math.min(15, 10 + c.rows.length) : (isSel ? 9 : 7);

                return (
                  <Marker
                    key={c.key}
                    position={{ lat: c.lat, lng: c.lng }}
                    title={grouped
                      ? `${c.rows.length} véhicules — ${c.rows.map((v) => v.plateNumber || '?').join(', ')}`
                      : `${r.plateNumber || 'Véhicule'} — ${STATE_LABEL[state]}`}
                    onClick={() => (grouped ? openCluster(c) : focus(r))}
                    icon={{
                      path: window.google.maps.SymbolPath.CIRCLE,
                      scale: size,
                      fillColor: STATE_COLOR[state],
                      fillOpacity: 1,
                      strokeColor: '#0D0C0B',
                      strokeWeight: grouped ? 2 : 2.5,
                      // The label sits inside the symbol, so a grouped bubble
                      // centres its count while a single dot pushes the plate
                      // clear of the circle. labelOrigin is in symbol units,
                      // multiplied by scale — hence the divide.
                      labelOrigin: new window.google.maps.Point(0, grouped ? 0 : 22 / size),
                    }}
                    label={grouped
                      ? {
                        text: String(c.rows.length),
                        color: '#0D0C0B',
                        fontSize: '11px',
                        fontWeight: '700',
                        fontFamily: 'Space Grotesk, sans-serif',
                      }
                      : {
                        text: r.plateNumber || '—',
                        color: isSel ? PLATE_GREEN_SELECTED : PLATE_GREEN,
                        fontSize: '10.5px',
                        fontWeight: '600',
                        fontFamily: 'Space Grotesk, sans-serif',
                      }}
                  />
                );
              })}
            </GoogleMap>
          ) : (
            <div style={{ height: '320px' }} className="flex items-center justify-center text-[12px] text-[#6E665C]">
              Chargement de la carte…
            </div>
          )}
        </div>

        <ul className="flex-1 min-w-[240px] basis-[300px] max-h-[320px] overflow-y-auto py-1.5">
          {rows.map((r) => {
            const state = vehicleState(r);
            const isSel = selected === String(r.vehicleId);
            return (
              <li key={String(r.vehicleId)}>
                <button
                  onClick={() => focus(r)}
                  onDoubleClick={() => onOpenVehicle && onOpenVehicle(r)}
                  className={`w-full text-left flex items-center gap-2.5 px-[18px] py-2.5 transition-colors
                              ${isSel ? 'bg-[#1A1613]' : 'hover:bg-[#1A1613]'}`}
                >
                  <span className="w-2 h-2 rounded-full shrink-0" style={{ background: STATE_COLOR[state] }} />
                  <span className="flex-1 min-w-0">
                    <span className="block font-['Space_Grotesk',sans-serif] text-[12.5px] text-[#F4F1EA] tabular-nums truncate">
                      {r.plateNumber || '—'}
                      {/* Admin payload only. A plate alone does not tell an
                          operator whose car has gone dark. */}
                      {r.ownerName && (
                        <span className="ml-1.5 font-['IBM_Plex_Sans',sans-serif] text-[11px] text-[#6E665C] tabular-nums-none">
                          {r.ownerName}
                        </span>
                      )}
                    </span>
                    <span className="block text-[11px] text-[#8F877B] truncate mt-0.5">{positionLabel(r)}</span>
                  </span>
                  {onOpenVehicle && (
                    <span
                      role="link"
                      tabIndex={-1}
                      onClick={(e) => { e.stopPropagation(); onOpenVehicle(r); }}
                      className="text-[11px] text-[#6E665C] hover:text-[#F4F1EA] shrink-0 transition-colors"
                    >
                      Détails
                    </span>
                  )}
                </button>
              </li>
            );
          })}
        </ul>
      </div>
    </div>
  );
}
